import { NavLink } from 'react-router-dom';
import { getAnonymousUser, isAuthEnabled, useCurrentUser } from '../auth';

const links = [
  { to: '/', label: 'Dashboard', icon: '\u25c8' },
  { to: '/items', label: 'Items', icon: '\u25a4' },
  { to: '/messaging', label: 'Messaging', icon: '\u25c7' },
  { to: '/upload', label: 'Upload', icon: '\u2191' },
  { to: '/chaos', label: 'Chaos', icon: '\u26a1' },
  { to: '/health', label: 'Health', icon: '\u2665' },
];

const sidebarStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: 240,
  height: '100vh',
  background: 'var(--color-sidebar)',
  color: 'var(--color-text-inverse)',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 100,
};

const logoStyle = {
  padding: '1.25rem 1.25rem 1rem',
  borderBottom: '1px solid rgba(255,255,255,0.08)',
};

const linkBaseStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  padding: '0.625rem 1.25rem',
  color: '#94a3b8',
  textDecoration: 'none',
  fontSize: '0.9rem',
  fontWeight: 500,
  transition: 'all 150ms',
  borderRadius: '6px',
  margin: '2px 0.5rem',
};

const activeStyle = {
  ...linkBaseStyle,
  color: '#ffffff',
  background: 'var(--color-sidebar-active)',
};

const userBoxStyle = {
  padding: '0.75rem 1.25rem',
  borderTop: '1px solid rgba(255,255,255,0.08)',
  fontSize: '0.75rem',
  color: '#94a3b8',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
};

const signOutStyle = {
  marginTop: '0.5rem',
  alignSelf: 'flex-start',
  background: 'transparent',
  border: '1px solid rgba(255,255,255,0.12)',
  color: '#cbd5e1',
  padding: '0.25rem 0.625rem',
  borderRadius: 4,
  fontSize: '0.7rem',
  cursor: 'pointer',
};

// Footer with user info + sign-out. Used only when Okta is configured \u2014
// hooks inside it call useOktaAuth which requires the <Security> provider.
function AuthenticatedFooter() {
  const { user, oktaAuth } = useCurrentUser();
  const signOut = () => {
    // signOut() clears the Okta session in the IdP, then redirects to
    // postLogoutRedirectUri (configured in OktaConfig). Without the Okta hop
    // a user would silently re-authenticate from the still-live SSO session
    // and "sign out" would feel broken.
    oktaAuth.signOut();
  };
  return (
    <div style={userBoxStyle}>
      <div style={{ color: '#475569', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Signed in
      </div>
      <div style={{ color: '#e2e8f0', fontWeight: 500, wordBreak: 'break-all' }}>
        {user.email}
      </div>
      <button type="button" style={signOutStyle} onClick={signOut}>
        Sign out
      </button>
    </div>
  );
}

function AnonymousFooter() {
  const user = getAnonymousUser();
  return (
    <div style={userBoxStyle}>
      <div style={{ color: '#475569', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Signed in
      </div>
      <div style={{ color: '#e2e8f0', fontWeight: 500, wordBreak: 'break-all' }}>
        {user.email}
      </div>
      <div style={{ color: '#64748b', fontSize: '0.65rem' }}>
        Okta SPA not configured (set VITE_OKTA_CLIENT_ID + VITE_OKTA_ISSUER)
      </div>
    </div>
  );
}

export default function Sidebar() {
  return (
    <aside style={sidebarStyle}>
      <div style={logoStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {/* Interim asset cropped from docs/demo-observability-app-diagram.png.
              Swap for the official SVG from the brand portal when available. */}
          <img
            src="/brand-mark.png"
            alt=""
            aria-hidden="true"
            style={{ height: 16, width: 'auto', flexShrink: 0 }}
          />
          <div style={{ fontSize: '1.125rem', fontWeight: 700, color: '#f1f5f9' }}>
            Demo Observability App
          </div>
        </div>
        <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>
          Observability Console
        </div>
      </div>
      <nav style={{ padding: '0.75rem 0', flex: 1, overflowY: 'auto' }}>
        {links.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            end={link.to === '/'}
            style={({ isActive }) => (isActive ? activeStyle : linkBaseStyle)}
          >
            <span style={{ fontSize: '1rem', width: 20, textAlign: 'center' }}>
              {link.icon}
            </span>
            {link.label}
          </NavLink>
        ))}
      </nav>
      {isAuthEnabled() ? <AuthenticatedFooter /> : <AnonymousFooter />}
    </aside>
  );
}
